import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronRight, Download, Share, Smartphone, X } from 'lucide-react';
import { usePwaInstallStore } from '@/stores/pwaInstall';
import { useAuthStore } from '@/stores/auth';
import { ModalOverlay } from './ModalOverlay';

export function InstallButton({ menu = false }: { menu?: boolean }) {
  const { installed, prompt, ios, busy, requestInstall } = usePwaInstallStore();
  if (installed || (!menu && !prompt && !ios)) return null;
  const label = ios
    ? 'Añadir al inicio'
    : prompt
      ? menu
        ? 'Instalar aplicación'
        : 'Instalar Camadero'
      : 'Cómo instalar la aplicación';
  return (
    <button
      type="button"
      className={menu ? 'ui-menu-row' : 'ui-secondary mt-4 w-full'}
      disabled={busy}
      onClick={() => void requestInstall()}
    >
      <Download size={menu ? 22 : 18} aria-hidden />
      <span>{label}</span>
      {menu && <ChevronRight size={18} aria-hidden />}
    </button>
  );
}

export function PwaInstallExperience() {
  const state = usePwaInstallStore();
  const user = useAuthStore((s) => s.user);
  const { pathname } = useLocation();
  const [offerOpen, setOfferOpen] = useState(false);
  useEffect(() => {
    usePwaInstallStore.setState((current) => ({
      contextVersion: current.contextVersion + 1,
      guide: null,
    }));
  }, [pathname, user?.id]);
  useEffect(() => {
    if (pathname !== '/' || !user) {
      setOfferOpen(false);
      return;
    }
    if (!state.installed && !state.offered && state.eligible && (state.prompt || state.ios)) {
      setOfferOpen(true);
      state.markOffered();
    }
  }, [pathname, user, state]);
  const closeOffer = () => {
    state.markOffered();
    setOfferOpen(false);
  };
  const canOffer =
    offerOpen &&
    pathname === '/' &&
    user &&
    !state.installed &&
    (state.prompt || state.ios) &&
    !state.guide;
  return (
    <>
      {canOffer && (
        <section className="ui-install-offer" aria-label="Instalar Camadero">
          <div className="flex items-start gap-3">
            <span className="ui-install-icon">
              <Smartphone size={24} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold">Camadero en tu móvil</h2>
              <p className="mt-1 text-sm text-text-secondary">
                Abre la aplicación desde tu pantalla de inicio.
              </p>
            </div>
            <button
              type="button"
              className="ui-icon-button"
              aria-label="Cerrar invitación de instalación"
              onClick={closeOffer}
            >
              <X size={20} aria-hidden />
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              className="ui-primary flex-1"
              disabled={state.busy}
              onClick={() => {
                setOfferOpen(false);
                void state.requestInstall();
              }}
            >
              {state.ios ? 'Añadir al inicio' : 'Instalar Camadero'}
            </button>
            <button type="button" className="ui-secondary" onClick={closeOffer}>
              Ahora no
            </button>
          </div>
        </section>
      )}
      {state.guide && !state.installed && (
        <ModalOverlay
          placement="bottom"
          className="max-w-md"
          onBackdrop={state.closeGuide}
          ariaLabelledby="install-guide-title"
          restoreFocusFallback={() =>
            document.querySelector<HTMLElement>(
              'nav[aria-label="Navegación principal"] a[href="/mas"]',
            ) ?? document.querySelector<HTMLElement>('main input, main button')
          }
        >
          <div className="flex items-center justify-between gap-3">
            <h2 id="install-guide-title" className="text-lg font-semibold">
              {state.guide === 'ios' ? 'Añadir Camadero al inicio' : 'Instalar Camadero'}
            </h2>
            <button
              type="button"
              className="ui-icon-button"
              aria-label="Cerrar instrucciones"
              onClick={state.closeGuide}
            >
              <X size={20} aria-hidden />
            </button>
          </div>
          <p className="mt-2 text-sm text-text-secondary">
            Tendrás un icono de Camadero en tu pantalla de inicio.
          </p>
          {state.guide === 'ios' ? (
            <ol className="ui-install-steps">
              <li>
                <span>1</span>
                <p>
                  Abre Camadero en <strong>Safari</strong>.
                </p>
              </li>
              <li>
                <span>2</span>
                <p>
                  Pulsa <strong>Compartir</strong>{' '}
                  <Share size={17} className="inline-block align-text-bottom" aria-hidden /> en el
                  menú del navegador.
                </p>
              </li>
              <li>
                <span>3</span>
                <p>
                  Elige <strong>Añadir a pantalla de inicio</strong>. Mantén «Abrir como app web»
                  activado si aparece y pulsa <strong>Añadir</strong>.
                </p>
              </li>
            </ol>
          ) : (
            <div className="my-4 text-sm leading-relaxed text-text-secondary">
              <p>
                Abre el menú de tu navegador y busca <strong>Instalar aplicación</strong> o{' '}
                <strong>Añadir a pantalla de inicio</strong>.
              </p>
              <p className="mt-2">
                Si no aparece esa opción, prueba con Chrome en Android o Safari en iPhone.
              </p>
            </div>
          )}
          <button type="button" className="ui-primary w-full" onClick={state.closeGuide}>
            Entendido
          </button>
        </ModalOverlay>
      )}
    </>
  );
}
